import { App, BackButtonListener } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { SplashScreen } from "@capacitor/splash-screen";
import { f7ready } from "framework7-react";
import { useEffect } from "react";

import { useIsEnabledCapacitor } from "@/hooks/useIsEnabledCapacitor";

export const useInitializeCapacitor = () => {
  const isEnabledCapacitor = useIsEnabledCapacitor();

  useEffect(() => {
    if (!isEnabledCapacitor) return;

    f7ready((f7) => {
      const handleBackButton: BackButtonListener = () => {
        const $ = f7.$;
        const currentView = f7.views.current;

        if ($(".actions-modal.modal-in").length) {
          f7.actions.close(".actions-modal.modal-in");
          return;
        }

        if ($(".dialog.modal-in").length) {
          f7.dialog.close(".dialog.modal-in");
          return;
        }

        if ($(".sheet-modal.modal-in").length) {
          f7.sheet.close(".sheet-modal.modal-in");
          return;
        }

        if ($(".popover.modal-in").length) {
          f7.popover.close(".popover.modal-in");
          return;
        }

        if ($(".popup.modal-in").length) {
          if ($(".popup.modal-in>.view").length) {
            const currentView = f7.views.get(".popup.modal-in>.view");
            if (currentView && currentView.router && currentView.router.history.length > 1) {
              currentView.router.back();
              return;
            }
          }
          f7.popup.close(".popup.modal-in");
          return;
        }

        if ($(".login-screen.modal-in").length) {
          f7.loginScreen.close(".login-screen.modal-in");
          return;
        }

        if ($(".page-current .searchbar-enabled").length) {
          f7.searchbar.disable(".page-current .searchbar-enabled");
          return;
        }

        if ($(".page-current .card-expandable.card-opened").length) {
          f7.card.close(".page-current .card-expandable.card-opened");
          return;
        }

        if (currentView && currentView.router && currentView.router.history.length > 1) {
          currentView.router.back();
          return;
        }

        if ($(".panel.panel-in").length) {
          f7.panel.close(".panel.panel-in");
          return;
        }
      };

      void App.addListener("backButton", handleBackButton);

      return () => {
        void App.removeAllListeners();
      };
    });
  }, [isEnabledCapacitor]);

  useEffect(() => {
    if (!isEnabledCapacitor) return;

    if (Capacitor.isPluginAvailable("Keyboard")) {
      void Keyboard.setResizeMode({ mode: KeyboardResize.Native });
      void Keyboard.setScroll({ isDisabled: true });
      void Keyboard.setAccessoryBarVisible({ isVisible: false });
    }
  }, [isEnabledCapacitor]);

  useEffect(() => {
    if (!isEnabledCapacitor) return;

    f7ready((f7) => {
      const handleKeyboardWillShow = () => {
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement) {
          f7.input.scrollIntoView(activeElement, 0, true, true);
        }
      };

      window.addEventListener("keyboardWillShow", handleKeyboardWillShow);

      return () => {
        window.removeEventListener("keyboardWillShow", handleKeyboardWillShow);
      };
    });
  }, [isEnabledCapacitor]);

  useEffect(() => {
    if (!isEnabledCapacitor) return;

    f7ready((f7) => {
      const handleKeyboardDidShow = () => {
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement) {
          f7.input.scrollIntoView(activeElement, 0, true, true);
        }
      };
      window.addEventListener("keyboardDidShow", handleKeyboardDidShow);

      return () => {
        window.removeEventListener("keyboardDidShow", handleKeyboardDidShow);
      };
    });
  }, [isEnabledCapacitor]);

  useEffect(() => {
    if (!isEnabledCapacitor) return;

    f7ready((f7) => {
      const $ = f7.$;

      const handleKeyboardDidHide: EventListenerOrEventListenerObject = () => {
        const activeElement = document.activeElement;
        if (activeElement && $(activeElement).parents(".messagebar").length) {
          return;
        }
        if (Capacitor.isPluginAvailable("Keyboard")) {
          void Keyboard.setAccessoryBarVisible({ isVisible: true });
        }
      };
      window.addEventListener("keyboardDidHide", handleKeyboardDidHide);

      return () => {
        window.removeEventListener("keyboardDidHide", handleKeyboardDidHide);
      };
    });
  }, [isEnabledCapacitor]);

  useEffect(() => {
    if (!isEnabledCapacitor) return;

    const handleTouchStart = (e: Event) => {
      const target = e.target as (HTMLElement & { type?: string }) | null;
      if (!target) return;
      const nodeName = target.nodeName.toLowerCase();
      const type = (target as HTMLInputElement).type;
      const showForTypes = ["datetime-local", "time", "date", "datetime"];
      if (Capacitor.isPluginAvailable("Keyboard")) {
        if (nodeName === "select" || showForTypes.includes(type)) {
          void Keyboard.setAccessoryBarVisible({ isVisible: true });
        } else {
          void Keyboard.setAccessoryBarVisible({ isVisible: false });
        }
      }
    };

    document.addEventListener("touchstart", handleTouchStart, true);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart, true);
    };
  }, [isEnabledCapacitor]);

  useEffect(() => {
    setTimeout(() => {
      void SplashScreen.hide();
    }, 2000);
  }, []);
};
